import { useEffect, useRef, useState } from 'react'
import { Home, MapPin, Store, ShoppingBag, Gift, QrCode, Receipt } from 'lucide-react'
import Screenshots from './Screenshots'

// Scroll-driven product tour: the phone stays pinned while the screen
// crossfades through the real app screenshots as the visitor scrolls.
type Body = { src: string; ratio: number; top: number; bottom: number }

const steps: { src: string; body?: Body; icon: typeof Home; title: string; text: string; chip: string }[] = [
  {
    src: '/screenshots/v2/home.jpg',
    body: { src: '/screenshots/v2/home-body.jpg', ratio: 1735 / 720, top: 195, bottom: 1447 },
    icon: Home,
    title: 'Everything in one place',
    text: 'Your QR code, bills, orders and latest activity - all a tap away from the home screen.',
    chip: 'Your local hub',
  },
  {
    src: '/screenshots/v2/discover.jpg',
    icon: MapPin,
    title: 'Find shops near you',
    text: 'Search by town or postcode and see local restaurants, grocers and cafés on your doorstep.',
    chip: 'Shops nearby',
  },
  {
    src: '/screenshots/v2/shop.jpg',
    icon: Store,
    title: 'Browse the full shop',
    text: "Real products and real prices, straight from the shop's own till.",
    chip: 'Live menu',
  },
  {
    src: '/screenshots/v2/checkout.jpg',
    icon: ShoppingBag,
    title: 'Order in a tap',
    text: 'Choose pickup or delivery, add a note, and place your order in seconds.',
    chip: 'Pickup or delivery',
  },
  {
    src: '/screenshots/v2/rewards.jpg',
    body: { src: '/screenshots/v2/rewards-body.jpg', ratio: 2174 / 720, top: 195, bottom: 1447 },
    icon: Gift,
    title: 'Earn rewards as you shop',
    text: 'Every visit adds up. Watch your points grow and rewards unlock at the shops you love.',
    chip: 'Rewards unlocked',
  },
  {
    src: '/screenshots/v2/qr.jpg',
    icon: QrCode,
    title: 'Show your QR at the till',
    text: 'One secure code that refreshes every few minutes. The cashier scans it and your points land instantly.',
    chip: 'Points in a scan',
  },
  {
    src: '/screenshots/v2/bills.jpg',
    icon: Receipt,
    title: 'Every bill, saved for you',
    text: 'Digital receipts from every shop, with the points you earned on each one.',
    chip: 'Points on every bill',
  },
]

const SCROLL_PER_STEP = 70 // vh of scrolling per step

const AppStory = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0) // 0..1 across the whole section
  const [reduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    if (reduced) return
    let raf = 0
    const update = () => {
      raf = 0
      const el = ref.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = rect.height - window.innerHeight
      setProgress(Math.min(1, Math.max(0, -rect.top / Math.max(total, 1))))
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [reduced])

  if (reduced) return <Screenshots />

  const active = Math.min(steps.length - 1, Math.floor(progress * steps.length))

  const goTo = (i: number) => {
    const el = ref.current
    if (!el) return
    const total = el.offsetHeight - window.innerHeight
    const top = el.getBoundingClientRect().top + window.scrollY + ((i + 0.5) / steps.length) * total
    window.scrollTo({ top, behavior: 'smooth' })
  }

  return (
    <section id="screenshots" aria-label="See Nearbies in action">
      <div
        ref={ref}
        style={{ height: `calc(100vh + ${steps.length * SCROLL_PER_STEP}vh)` }}
        className="relative"
      >
        <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
          <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 lg:mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                See Nearbies in Action
              </h2>
              <p className="text-base sm:text-xl text-gray-600">
                Keep scrolling - from finding a shop to earning rewards
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 lg:gap-16 items-center">
              {/* Step text */}
              <div className="order-2 lg:order-1">
                {/* Desktop: full step list */}
                <ol className="hidden lg:block space-y-3">
                  {steps.map((s, i) => {
                    const Icon = s.icon
                    const on = i === active
                    return (
                      <li key={s.title}>
                        <button
                          type="button"
                          onClick={() => goTo(i)}
                          className={`w-full text-left flex gap-4 p-4 rounded-2xl border transition-all duration-300 ${
                            on
                              ? 'bg-white shadow-lg border-primary-200 scale-100'
                              : 'border-transparent opacity-50 hover:opacity-80 scale-95'
                          }`}
                        >
                          <span
                            className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                              on ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                          </span>
                          <span>
                            <span className="block font-semibold text-gray-900">{s.title}</span>
                            <span
                              className={`block text-gray-600 overflow-hidden transition-all duration-300 ${
                                on ? 'max-h-24 mt-1 opacity-100' : 'max-h-0 opacity-0'
                              }`}
                            >
                              {s.text}
                            </span>
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ol>

                {/* Mobile: only the active step */}
                <div className="lg:hidden text-center" aria-live="polite">
                  <div key={active} className="animate-step-in">
                    <h3 className="text-xl font-bold text-gray-900">{steps[active].title}</h3>
                    <p className="text-gray-600 mt-1 text-sm sm:text-base">{steps[active].text}</p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="order-1 lg:order-2 flex justify-center">
                <div className="relative">
                  <div className="relative w-[26vh] max-w-[240px] lg:w-[270px] lg:max-w-none rounded-[2.2rem] border-[7px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden aspect-[720/1610]">
                    {steps.map((s, i) => {
                      // Local 0..1 scroll of the screen's own content, driven by page scroll
                      const local = progress * steps.length - i
                      const p = Math.min(1, Math.max(0, (local - 0.12) / 0.7))
                      const eased = p * p * (3 - 2 * p)
                      const body = s.body
                      const areaH = body ? (body.bottom - body.top) / 720 : 0
                      const shift = body ? (1 - areaH / body.ratio) * 100 * eased : 0
                      return (
                        <div
                          key={s.src}
                          className="absolute inset-0 bg-gray-100 transition-all duration-500 ease-out"
                          style={{
                            opacity: i === active ? 1 : 0,
                            transform:
                              i === active
                                ? 'translateY(0) scale(1)'
                                : i < active
                                  ? 'translateY(-4%) scale(0.97)'
                                  : 'translateY(4%) scale(1.03)',
                          }}
                        >
                          <img
                            src={s.src}
                            alt={s.title}
                            loading={i === 0 ? 'eager' : 'lazy'}
                            className="absolute inset-0 w-full h-full object-cover object-top"
                          />
                          {body && (
                            <div
                              className="absolute inset-x-0 overflow-hidden"
                              style={{
                                top: `${(body.top / 1610) * 100}%`,
                                height: `${((body.bottom - body.top) / 1610) * 100}%`,
                              }}
                            >
                              <img
                                src={body.src}
                                alt=""
                                loading="lazy"
                                className="block w-full h-auto will-change-transform"
                                style={{ transform: `translateY(-${shift}%)` }}
                              />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* Floating callout chip */}
                  <div
                    key={active}
                    className="animate-chip-in absolute -left-3 lg:-left-16 top-1/4 bg-white rounded-full shadow-xl border border-primary-100 px-4 py-2 text-sm font-semibold text-primary-700 whitespace-nowrap"
                  >
                    {steps[active].chip}
                  </div>

                  {/* Progress dots */}
                  <div className="absolute -right-5 lg:-right-8 top-1/2 -translate-y-1/2 flex flex-col gap-2">
                    {steps.map((s, i) => (
                      <button
                        key={s.title}
                        type="button"
                        aria-label={`Go to: ${s.title}`}
                        onClick={() => goTo(i)}
                        className={`w-2 rounded-full transition-all duration-300 ${
                          i === active ? 'h-6 bg-primary-600' : 'h-2 bg-gray-300 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AppStory
